import {
  CalendarOutlined,
  CloseOutlined,
  FireFilled,
  FlagOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { Button, Link } from '@mm/components';
import { CopyIcon } from '@mm/icons';

import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { enhancedPlansSelector } from '@mm/state/selectors/subscriptionSelectors';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import {
  selectActiveChatbot,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as EntrepreneurLogo } from 'assets/entrepreneur.svg';
import { ReactComponent as UnicornLandText } from 'assets/unicorn_land_text.svg';
import { isArray } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { ToggleSwitch } from '../../components/ToggleSwitch/ToggleSwitch';
import { successToast } from '../../helpers/toast';
import { useFreeTrialUpgradeModal } from '../FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import css from './SubscribeAfterTrialPromoModal.module.css';

export const SubscribeAfterTrialPromoModal = ({
  onSuccessBilling,
}: {
  onSuccessBilling?: () => void;
}) => {
  const history = useHistory();
  const companyId = useSelector(selectActiveCompanyId);
  const { updateUserAttributes } = useUsersCRUDActions();
  const { id: userId, userAttributes } = useSelector(selectSessionSelector);
  const { isExpired, isSendLimitReached } = useFreeTrialUpgradeModal();
  const plans = useSelector(enhancedPlansSelector);
  const modalActions = useModalActions();
  const facebookPage = useSelector(activeFacebookPageSelector);
  const plansMapping = useSelector(
    (state: StoreState) => state.subscription.plansMapping,
  );
  const chatbotId = useSelector(selectActiveChatbot)?.id;
  const { getPlans } = useSubscriptionCRUDActions();

  const [isMonthly, setIsMonthly] = useState(true);
  const [code, setCode] = useState<string>();
  const [plan, setPlan] = useState<EnhancedPlan>();

  useEffect(() => {
    if (!userAttributes.trialPaywallSeen) {
      updateUserAttributes(companyId, userId, { trialPaywallSeen: true });
    }
  }, [userAttributes.trialPaywallSeen]);

  useEffect(() => {
    if (!companyId) {
      return;
    }
    (async () => {
      if (!plansMapping) {
        await getPlans(companyId);
      } else {
        const planCode = isArray(plansMapping.unicorn)
          ? plansMapping.unicorn[isMonthly ? 0 : 1]
          : plansMapping.unicorn; // Not sure when this is possible
        const enhancedPlan = plans.find(p => p.planCode === planCode);
        setCode(planCode);
        setPlan(enhancedPlan);
      }
    })();
  }, [code, plansMapping, companyId, isMonthly]);

  const period = isMonthly ? 'month' : 'year';

  const isTrialOver = isExpired || isSendLimitReached;

  const monthly = period === 'month';

  const expireDate = moment()
    .add(moment().isoWeekday() > 5 ? 1 : 0, 'weeks')
    .startOf('week')
    .add('days', 5)
    .format('MMM Do');

  // Note: I believe we migh want a special offer back again, so making the modal
  // more configurable.
  const offerDiscount = false;

  return (
    <div className={css.container}>
      <div className={css.header}>
        <div className={css.badgeContainer}>
          <div className={css.badge}>
            <div className={css.badgeRow1}>Top 50</div>
            <div className={css.badgeRow2}>Products of all time</div>
            <div className={css.badgeRow3}>2022</div>
          </div>
          <div className={css.badgeTriangle} />
        </div>
        <div className={css.title}>
          <div className={css.titleRow1}>Best sales and Marketing Software</div>
          <div className={css.titleRow2}>
            <EntrepreneurLogo />
          </div>
        </div>
        <div
          className={css.headerClose}
          onClick={() => modalActions.closeModal()}
        >
          <CloseOutlined />
        </div>
      </div>
      <div className={css.body}>
        <div className={css.expiredNotice}>
          <div className={css.expiredNoticeTitle}>
            <UnicornLandText />
            <br />
            has never been closer
          </div>
          {offerDiscount && (
            <div className={css.expiredNoticeInfo}>
              Save up to 50% on the first three months.
            </div>
          )}
        </div>
        {!plan ? (
          <div className={css.expiredContactSupport}>
            Please contact us at
            <Link
              className={css.expiredContactSupportLink}
              capitalize={false}
              href={`mailto:support@customers.ai?subject=${encodeURI(
                `Free Trial Upgrade Error: no corresponding plan available`,
              )}`}
            >
              support@customers.ai
            </Link>
            to upgrade.
          </div>
        ) : (
          <>
            <div className={css.periodSwitcher}>
              <div className={css.periodSwitcherSwitch}>
                <ToggleSwitch
                  isOn={!isMonthly}
                  leftSwitchText="Monthly"
                  rightSwitchtext="Annual"
                  handleToggle={() => setIsMonthly(!isMonthly)}
                  classes={{ trigger: css.periodSwitcherSwitchtTrigger }}
                />
              </div>
            </div>
            <div className={css.offer}>
              {offerDiscount && (
                <div className={css.offerBadge}>
                  <div className={css.offerBadgeIcon}>
                    <FireFilled />
                  </div>
                  <div className={css.offerBadgeText}>
                    Offer expires {expireDate}
                  </div>
                  <div className={css.offerBadgeTriangle} />
                </div>
              )}

              <div className={css.planSwitcher}>
                <div className={css.planPriceRow}>
                  {monthly && offerDiscount ? (
                    <>
                      <div className={css.priceWas}>
                        ${(plan?.unitAmountInCents || 0) / 100}{' '}
                      </div>
                      <div className={css.price}>$59</div>
                    </>
                  ) : (
                    <div className={css.price}>
                      ${(plan?.unitAmountInCents || 0) / 100}{' '}
                    </div>
                  )}

                  <div className={css.planPeriod}>/ {period}</div>

                  {offerDiscount && monthly && (
                    <span className={css.pricePromoInfo}>
                      for the first 3 months
                    </span>
                  )}
                </div>
              </div>

              {offerDiscount && monthly && (
                <div className={css.coupon}>
                  <span className={css.couponTitle}>
                    Use Your Unique Coupon Code:
                  </span>

                  <div className={css.code}>
                    earlybird-2xh6fy8
                    <CopyToClipboard
                      text="earlybird-2xh6fy8"
                      onCopy={() => successToast('Copied', 'top-right')}
                    >
                      <CopyIcon className={css.copyIcon} />
                    </CopyToClipboard>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className={css.features}>
          <div className={css.feature}>
            <div className={css.featureIconContainer}>
              <FlagOutlined className={css.featureIcon} />
            </div>
            <div className={css.featureText}>
              Automatically follow up on every lead
            </div>
          </div>
          <div className={css.feature}>
            <div className={css.featureIconContainer}>
              <CalendarOutlined className={css.featureIcon} />
            </div>
            <div className={css.featureText}>Book more qualified meetings</div>
          </div>
          <div className={css.feature}>
            <div className={css.featureIconContainer}>
              <TrophyOutlined className={css.featureIcon} />
            </div>
            <div className={css.featureText}>
              Fill your pipeline & win more deals, faster
            </div>
          </div>
        </div>
        <div className={css.buttons}>
          <Button
            size="lg"
            disabled={!plan}
            onClick={() => {
              if (plan) {
                modalActions.openBillingModal({
                  facebookPage,
                  plan,
                  paywall: isTrialOver,
                  isMonthly,
                  hideAnnualToggle: true,
                  onSuccessBilling: () => {
                    if (onSuccessBilling) {
                      onSuccessBilling();
                    } else {
                      if (chatbotId) {
                        const link = BotBuilderRoutes.automationHome.getLink({
                          chatbotId,
                        });
                        setTimeout(() => history.push(link), 100);
                      }
                    }
                  },
                });
              }
            }}
          >
            Checkout
          </Button>
        </div>
      </div>
    </div>
  );
};
